import {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  type User,
  fetchCurrentUser,
  logout as doLogout,
  redirectToLogin,
} from "@/auth/auth";

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  logout: () => void;
  refetch: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component.
 *
 * Provides authentication state and actions to the component tree.
 * Includes a global 401 interceptor that redirects to login on auth failures.
 */
export function AuthProvider({ children }: Readonly<AuthProviderProps>) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const u = await fetchCurrentUser();
      setUser(u);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Global 401 interceptor – use a ref to ensure only one interceptor is
  // installed even if the component re-mounts (e.g. React StrictMode).
  const interceptorInstalled = useRef(false);
  useEffect(() => {
    if (interceptorInstalled.current) return;
    interceptorInstalled.current = true;

    const originalFetch = globalThis.fetch;
    globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await originalFetch(input, init);
      if (response.status === 401 && !response.url.includes("/api/auth/")) {
        redirectToLogin();
      }
      return response;
    };
    return () => {
      globalThis.fetch = originalFetch;
      interceptorInstalled.current = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAdmin: user?.isAdmin ?? false,
      logout: doLogout,
      refetch,
    }),
    [user, isLoading, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
